
import { Component, Vue } from "vue-property-decorator";
import FeedDeliverySummary from "@/views/FeedDeliverySummary.vue";
import CribsSummaryPlan from "./CribPlan.vue";
import FeedSchedule from "./FeedSchedule.vue";
import FeedPlan from "./FeedPlan.vue";
import FeedMixer from "./FeedMixer.vue";
import { createClient } from "@/helpers/graphql-ws-rtc-adapter";

import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { homeBreadcrumbs } from "@/utils/breadcrumbs";
import { ReplayCribSnapshot, summarizeReplayChanges } from "@/utils/replayChanges";

const Subscriber = require("@jetblack/graphql-reconnect-client");

@Component({
  components: {
    FeedDeliverySummary,
    CribsSummaryPlan,
    FeedSchedule,
    FeedPlan,
    FeedMixer,
    BreadcrumbTrail,
  },
})
export default class Replay extends Vue {
  defaultDateSelected = this.formatDate(new Date());
  dateSelected = this.formatPickerDate(new Date());
  dateMenu = false;
  frameTimeRaw: any = null;
  frameLog: { id: number; time: string; changes: string[] }[] = [];
  frameLogFilter: string | null = "";

  get filteredFrameLog() {
    const query = (this.frameLogFilter || "").toLowerCase();
    if (!query) return this.frameLog;
    return this.frameLog
      .map((entry) => ({ ...entry, changes: entry.changes.filter((change) => change.toLowerCase().includes(query)) }))
      .filter((entry) => entry.changes.length > 0);
  }

  previousFrame: ReplayCribSnapshot[] | null = null;
  previousFrameTime: string | null = null;
  nextLogId = 0;
  activeTab = 0;
  tabs = [
    { title: "Ration Summary", icon: "mdi-chart-box-outline" },
    { title: "Crib Scores", icon: "mdi-altimeter" },
    { title: "Feeding Schedule", icon: "mdi-nutrition" },
    { title: "Feed Plan", icon: "mdi-clipboard-text-outline" },
    { title: "Mixing Schedule", icon: "mdi-mixer" },
  ];

  mounted() {
    this.$store.dispatch("user/addFirebaseCallback", this.getReplayFrameSocket);
  }

  destroyed() {
    if (this.graphQLUnsubscribe) {
      this.graphQLUnsubscribe();
    }
    if (this.graphQLSubscriber) {
      this.graphQLSubscriber();
    }
  }

  isPlaying = false;

  async togglePlay() {
    if (this.isPlaying) {
      await this.pausePlayback();
    } else {
      await this.startPlayback();
    }
  }

  get frameTimeDisplay() {
    if (!this.frameTimeRaw) return "Waiting for frame";
    const frameDate = this.parseFrameDate(this.frameTimeRaw);
    if (!frameDate) return String(this.frameTimeRaw);
    return this.$store.state.moment(frameDate).format("DD MMM YYYY HH:mm:ss");
  }

  get replayFrameSubscription() {
    return `subscription($guid: String!) {
      replaycribScores(guid: $guid) {
        typename
        guid
        date
        frameTime
        cribScores {
          guid
          kraalGuid
          kraalId
          score
          call
          status
          ration
          actualFed
          animalCount
          sequence
        }
      }
    }`;
  }

  get replayVariables() {
    return { guid: this.$store.state.user.location.guid };
  }

  graphQLSubscriber: any = undefined;
  graphQLUnsubscribe: any = undefined;
  urlws = "wss://pi.gigalot.systems:7778/api/ws";
  options = {};
  operationName = null;

  async getReplayFrameSocket() {
    if (this.$store.state.useP2PConn) {
      await this.getOnlineReplayFrameSocket();
    } else {
      await this.getOnsiteReplayFrameSocket();
    }
  }

  updateFrameTimeFromReplayData(data: any) {
    const frame = data?.replaycribScores;
    if (!frame) return;
    const frameTime = frame.frameTime;
    this.frameTimeRaw = frameTime ?? null;
    const snapshot: ReplayCribSnapshot[] | undefined = data?.replaycribScores?.cribScores;
    if (!Array.isArray(snapshot)) return;
    const timeKey = String(this.parseFrameDate(frameTime)?.getTime() ?? frameTime ?? "");
    const changes = this.previousFrame === null ? [`Initial frame: ${snapshot.length} kraals loaded.`] : summarizeReplayChanges(this.previousFrame, snapshot);
    if (this.previousFrame !== null && timeKey === this.previousFrameTime && !changes.length) return;
    this.previousFrame = snapshot.map((item) => ({ ...item }));
    this.previousFrameTime = timeKey;
    const followingLogs = ["frameLog", "filteredFrameLog"].filter((ref) => {
      const element = this.$refs[ref] as HTMLElement | undefined;
      return !element || element.scrollHeight - element.scrollTop - element.clientHeight < 32;
    });
    this.frameLog.push({
      id: this.nextLogId++,
      time: frameTime ? this.frameTimeDisplay : "Time unavailable",
      changes: changes.length ? changes : ["No key changes."],
    });
    this.frameLog = this.frameLog.slice(-100);
    this.$nextTick(() => {
      followingLogs.forEach((ref) => {
        const element = this.$refs[ref] as HTMLElement | undefined;
        if (element) element.scrollTop = element.scrollHeight;
      });
    });
  }

  async getOnsiteReplayFrameSocket() {
    this.graphQLSubscriber = Subscriber.graphQLReconnectingSubscriber(
      this.urlws,
      this.options,
      (error: any, subscribe: any) => {
        try {
          if (!(error || subscribe)) {
            this.$store.state.wsDialogVisible = false;
            return;
          }
          if (error) {
            if (error.event.type !== "close") {
              this.$store.state.wsDialogVisible = true;
              console.error(error);
            }
          }
          this.graphQLUnsubscribe = subscribe(this.replayFrameSubscription, this.replayVariables, this.operationName, (error: any, data: any) => {
            if (!(error || subscribe)) {
              this.$store.state.wsDialogVisible = false;
              return;
            }
            if (error) {
              if (error.event.type !== "close") {
                this.$store.state.wsDialogVisible = true;
                console.error(error);
              }
              return;
            }
            this.$store.state.wsDialogVisible = false;
            this.updateFrameTimeFromReplayData(data);
          });
        } catch (err) {
          console.log(err);
        }
      },
      15000,
      3,
      "graphql-ws"
    );
  }

  async getOnlineReplayFrameSocket() {
    const variables = { guid: this.$store.state.user.location.guid, user: this.$store.state.user.user.uid };

    const subscribe = (subscriptions: string, variables: any, callback: (error: any, data: any) => Promise<void>) => {
      const client = createClient();
      let unsubscribe = client?.subscribe(
        {
          query: subscriptions,
          variables: variables,
        },
        {
          next: (message: any) => callback(undefined, message.data),
          error: (err: any) => {
            throw err;
          },
          complete: () => {
            console.log("subscription completed");
          },
        }
      );
      return unsubscribe;
    };

    this.graphQLUnsubscribe = subscribe(this.replayFrameSubscription, variables, async (error: any, data: any) => {
      this.$store.state.wsConnected = true;
      if (!(error || subscribe)) {
        this.$store.state.wsDialogVisible = false;
        return;
      }
      if (error) {
        this.$store.state.wsConnected = false;
        if (error.event.type !== "close") {
          this.$store.state.wsDialogVisible = true;
          console.error(error);
        }
        return;
      }
      this.$store.state.wsDialogVisible = false;
      this.$store.state.wsConnected = true;
      this.updateFrameTimeFromReplayData(data);
    });
  }

  async runPlaybackMutation(command: "startPlayback" | "pausePlayback" | "stepForward" | "stepBack" | "fastForward" | "rewind" | "loadPlayback") {
    const loading = command === "loadPlayback";
    const gql = `mutation($guid: String!${loading ? ", $date: String!" : ""}) {
      ${command}(guid: $guid${loading ? ", date: $date" : ""})
    }`;
    await this.$store.dispatch("graphQl", {
      gql,
      variables: {
        ...this.replayVariables,
        ...(loading ? { date: this.defaultDateSelected } : {}),
      },
    });
  }

  parseFrameDate(value: any): Date | undefined {
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    if (typeof value === "number") {
      const date = new Date(value < 100000000000 ? value * 1000 : value);
      return Number.isNaN(date.getTime()) ? undefined : date;
    }
    if (typeof value !== "string") return undefined;

    const trimmed = value.trim();
    if (!trimmed) return undefined;

    if (/^\d+$/.test(trimmed)) {
      return this.parseFrameDate(Number(trimmed));
    }

    const date = new Date(trimmed);
    if (!Number.isNaN(date.getTime())) return date;

    const isoLikeDate = new Date(trimmed.replace(" ", "T"));
    if (!Number.isNaN(isoLikeDate.getTime())) return isoLikeDate;

    const dayFirstMatch = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
    if (!dayFirstMatch) return undefined;
    const [, day, month, year, hour = "00", minute = "00", second = "00"] = dayFirstMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  async startPlayback() {
    await this.runPlaybackMutation("startPlayback");
    this.isPlaying = true;
  }

  async pausePlayback() {
    await this.runPlaybackMutation("pausePlayback");
    this.isPlaying = false;
  }

  async stepForward() {
    await this.runPlaybackMutation("stepForward");
    this.isPlaying = false;
  }
  async stepBack() {
    await this.runPlaybackMutation("stepBack");
    this.isPlaying = false;
  }

  async fastForward() {
    await this.runPlaybackMutation("fastForward");
    this.isPlaying = true;
  }
  async rewind() {
    await this.runPlaybackMutation("rewind");
    this.isPlaying = true;
  }

  async loadReplay() {
    this.frameLog = [];
    this.previousFrame = null;
    this.previousFrameTime = null;
    this.frameTimeRaw = null;
    console.log(`Date selected: ${this.dateSelected}`);
    if (this.dateSelected.indexOf("-") == 4) {
      this.defaultDateSelected = this.reverseDateSelected(this.dateSelected);
    }
    await this.runPlaybackMutation("loadPlayback");
  }

  reverseDateSelected(date: string) {
    return `${date.slice(8, 10)}-${date.slice(5, 7)}-${date.slice(0, 4)}`;
  }

  onDateSelected() {
    this.dateMenu = false;
    this.loadReplay();
  }

  formatDate(date: any) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatPickerDate(date: Date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  get breadcrumbs() {
    return homeBreadcrumbs({ text: "Replay", disabled: true });
  }
}
